import intersectionBy from 'lodash/intersectionBy';
import React, { useState, useEffect } from 'react';
import { Form, Button, CloseButton } from 'react-bootstrap';

import BayFloatManager from '../../BayFloatManager';
import BaySelect from '../../BaySelect';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useSearchParams = (): [URLSearchParams] => {
	const params = new URLSearchParams(window.location.search);
	return [params];
};

interface SelectOption {
	label: string;
	value: string | boolean;
}

interface FilterState {
	visibility: SelectOption | null;
	fileTypes: SelectOption[];
}

interface FilterDrawerProps {
	t: (key: string) => string;
	showFilterDrawer: boolean;
	setShowFilterDrawer: (show: boolean) => void;
	onSubmit?: (filters: { visibility?: boolean | null; fileTypes: string[] }, clear?: boolean) => void;
	fileTypes?: string[];
	visibilityOptions?: SelectOption[];
}

// Helper function to format options for selects
const formatSelectOptions = (options: unknown[], isMulti = false): SelectOption[] => {
	if (!options || !Array.isArray(options)) return [];

	return options.map((option) => {
		// Remove the starting '.' from the option if it exists
		let processedOption = option;
		if (isMulti && typeof option === 'string') {
			processedOption = option.startsWith('.') ? option.substring(1) : option;
		}

		if (typeof processedOption === 'string') {
			return { label: processedOption, value: processedOption };
		}

		if (typeof processedOption === 'object' && processedOption !== null) {
			const obj = processedOption as Record<string, unknown>;
			return {
				label: String(obj.label || obj.name || obj.value || ''),
				value: (obj.value ?? obj.id ?? '') as string | boolean,
			};
		}

		return { label: '', value: '' };
	});
};

const FilterDrawer: React.FC<FilterDrawerProps> = ({
	t,
	showFilterDrawer,
	setShowFilterDrawer,
	onSubmit,
	fileTypes = [],
	visibilityOptions = [
		{ label: 'Visible', value: false },
		{ label: 'Hidden', value: true },
	],
}) => {
	const [searchParams] = useSearchParams();
	const [filters, setFilters] = useState<FilterState>({
		visibility: null,
		fileTypes: [],
	});

	const formattedFileTypes = formatSelectOptions(fileTypes, true);

	// Reset filters when drawer is opened
	useEffect(() => {
		if (showFilterDrawer) {
			setFilters({
				visibility: searchParams.get('showHidden')
					? searchParams.get('showHidden') === 'true'
						? visibilityOptions[1]
						: visibilityOptions[0]
					: null,
				fileTypes: searchParams.get('fileTypes')
					? intersectionBy(
						formattedFileTypes,
						formatSelectOptions(searchParams.get('fileTypes')?.split(',') || []),
						'value',
					)
					: [],
			});
		}
	}, [showFilterDrawer, searchParams]);

	const handleVisibilityChange = (selectedOption: SelectOption | null) => {
		setFilters({
			...filters,
			visibility: selectedOption,
		});
	};

	const handleFileTypesChange = (selectedOptions: SelectOption[] | null) => {
		setFilters({
			...filters,
			fileTypes: selectedOptions || [],
		});
	};

	const handleApplyFilters = () => {
		onSubmit &&
			onSubmit({
				visibility: filters.visibility?.value as boolean | undefined,
				fileTypes: filters.fileTypes.map((option) => String(option.value)),
			});
		setShowFilterDrawer(false);
	};

	const handleClearFilters = () => {
		setFilters({
			visibility: null,
			fileTypes: [],
		});
		onSubmit &&
			onSubmit(
				{
					visibility: null,
					fileTypes: [],
				},
				true,
			);
	};

	const FilterDrawerHeader = ({ handleCloseDrawer }: { handleCloseDrawer: () => void }) => {
		return (
			<div className="d-flex flex-column w-100">
				<div className="d-flex align-item-center justify-content-between">
					<div className="d-flex align-items-center">
						<h2>{t('filter')}</h2>
					</div>
					<div className="d-flex align-items-center">
						<CloseButton className="me-0" onClick={handleCloseDrawer} />
					</div>
				</div>
				<div className="d-flex align-items-center">
					<h1 className="mb-0 mt-1 fw-500 text-truncate">
						{t('readyToShare')}
					</h1>
				</div>
			</div>
		);
	};

	return (
		// Migrated from BayFilterDrawer (react-bootstrap Offcanvas) to BayFloatManager. The
		// Offcanvas version overlapped the top nav: its `top` came from
		// `.bayfilterdrawer-no-banner`, which is 0-1-0 and therefore loses to Bootstrap's
		// own `.offcanvas-end { top: 0 }` on source order. BayFloatManager sets `top` inline
		// from `--bay-float-top`, so nothing can out-order it.
		<BayFloatManager
			placement="end"
			width={400}
			className="bay-files-grid-filter-drawer"
			backdropClassName="bay-files-grid-filter-drawer-backdrop"
			closeButton={false}
			initialFocus={-1}
			headerClassName="border-bottom p-3 px-4"
			bodyClassName="p-3 px-4"
			footerClassName="border-top p-3 px-4 d-block"
			footer={
				<div className="d-flex align-items-center justify-content-between">
					<Form.Group className="form-item">
						<Button
							className="me-4 text-800"
							variant="link"
							onClick={handleClearFilters}
						>
							{t('clearAll')}
						</Button>
					</Form.Group>
					<Form.Group className="form-item">
						<Button variant="primary" onClick={handleApplyFilters}>
							{t('applyFilter')}
						</Button>
					</Form.Group>
				</div>
			}
			title={
				<FilterDrawerHeader
					handleCloseDrawer={() => setShowFilterDrawer(false)}
				/>
			}
			show={showFilterDrawer}
			onClose={() => setShowFilterDrawer(false)}
		>
			<Form>
				<Form.Group className="mb-3">
					<Form.Label>{t('visibility')}</Form.Label>
					<BaySelect
						isClearable
						name="visibility-filter"
						optionsArray={visibilityOptions}
						placeholder={t('select')}
						{...(filters.visibility && { selectedValue: filters.visibility })}
						onChangeFunc={(selected) => handleVisibilityChange(selected as SelectOption | null)}
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>{t('type')}</Form.Label>
					<BaySelect
						allowSelectAll
						isClearable
						isMulti
						name="file-type-filter"
						optionsArray={formattedFileTypes}
						placeholder={t('select')}
						{...(filters.fileTypes && filters.fileTypes.length > 0 && { selectedValue: filters.fileTypes })}
						onChangeFunc={(selected) => handleFileTypesChange(selected as SelectOption[] | null)}
					/>
				</Form.Group>
			</Form>
		</BayFloatManager>
	);
};

export default FilterDrawer;
